import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from '../api.ts';
import {
  Directory,
  type DirectoryType,
  FullBleedList,
  InspectField,
  InspectMuted,
  InspectOverlay,
} from '../directory.tsx';
import { PersonIcon } from '../icons.tsx';
import { knownRegion, phonePlaceholder } from '../lib/phone-regions.ts';
import { RegionPicker } from '../work-mobile.tsx';
import { JoinLinkPanel } from '../join-link.tsx';
import { Invite, LoadARoster } from '../roster.tsx';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  LoadError,
  Row,
  Select,
  Spinner,
  Status,
  Table,
} from '../ui.tsx';

/**
 * People is **the drill, and only the drill** (owner correction 2026-09-07).
 *
 * The screen is one 3-phase exploratory drill on the shared rail-directory
 * shell (`directory.tsx`), the same 2-panel shape as Settings plus a right
 * slide-out:
 *
 * - **Phase 2 — the cohort column on the LEFT.** The role selector is not a
 *   header dropdown; it is the type column — Users/All, Staff, Occupants,
 *   Administrators, and each Team. Clicking a cohort is navigation, a prompt for
 *   which slice of the roster to look at, never a filter `<select>`.
 * - **Phase 3 — the list fills the content area.** Above it (in the sticky
 *   header, "filter above the last panel") sit SEARCH and the secondary filter
 *   (Site) — role is NOT here, it is phase 2. The list rows show only what an
 *   admin scans for — Name (with an Active/Invited status) · Email · Team(s) ·
 *   Notifications (only the channels on file — App/SMS/Email) · Role —
 *   respecting the active cohort + search + site.
 * - **Detail — a row opens the person's inspect overlay from the RIGHT.** It
 *   carries the full management surface and the per-person guardian follows.
 *
 * Everything that used to stack under the list is gone (B/C): the second
 * always-on Reach coverage table is now columns on the rows; "Awaiting reply"
 * is folded in as a status affordance on pending rows rather than a section;
 * guardian follows live only in the person overlay; and transfer-ownership moved
 * to Settings (org/account config). The list renders **edge-to-edge** — no card,
 * no "Everyone" title — with the count in the sticky header (D), and the primary
 * CTAs (Invite, Import people) open their form in the right overlay, not inline
 * (F).
 *
 * There is a **single** `getPeople` source for the whole screen.
 */
export function People({
  orgId,
  initialSiteId = '',
}: {
  orgId: string;
  /**
   * A building to pre-select in the site filter on open — set when a building
   * page's "Alert audience" drill-down opens this screen as the named list for
   * that building (reused rather than a duplicate roster table). Empty (the
   * default, and every rail visit) opens People unfiltered.
   */
  initialSiteId?: string;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  // The primary CTAs open in the right inspect overlay, never inline (F).
  const [cta, setCta] = useState<'none' | 'invite' | 'upload' | 'link'>('none');
  // Only an owner or admin can make the join link; a viewer is never offered it.
  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe });
  const myRole = me.data?.memberships.find((m) => m.org_id === orgId)?.role;
  const canMakeLink = myRole === 'owner' || myRole === 'admin';

  // Phase 2 is the cohort column; phase 3's secondary refinements are the sticky
  // header's search + site. `activeType` is one of
  // `all | staff | occupants | admins | team:<id>`.
  const [activeType, setActiveType] = useState('all');
  const [site, setSite] = useState(initialSiteId);
  const [search, setSearch] = useState('');

  // Selection (bulk actions) and the open person detail live at the screen level
  // so the bulk bar can ride in the shared sticky header (D) and the count can
  // sit beside the title.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailId, setDetailId] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);

  // The one source of truth for the whole screen (unfiltered — the drill narrows
  // client-side).
  const people = useQuery({
    queryKey: ['people', orgId, {}],
    queryFn: () => api.getPeople(orgId, {}),
  });
  const teams = useQuery({ queryKey: ['teams', orgId], queryFn: () => api.getTeams(orgId) });
  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });
  const guardianFollows = useQuery({
    queryKey: ['guardian-follows', orgId],
    queryFn: () => api.getGuardianFollows(orgId),
  });

  const invalidatePeople = () => {
    void qc.invalidateQueries({ queryKey: ['people', orgId] });
    // The roster query key is still used by the setup wizard's RosterManager.
    void qc.invalidateQueries({ queryKey: ['roster', orgId] });
  };
  const invalidateTeams = () => void qc.invalidateQueries({ queryKey: ['teams', orgId] });

  // Single-person mutations, driven from the detail overlay.
  const changeRole = useMutation({
    mutationFn: ({ userId, role: r }: { userId: string; role: AssignableRole }) =>
      api.setRole(orgId, userId, r),
    onSuccess: invalidatePeople,
    onError: (e: Error) => setNote(e.message),
  });
  const removePerson = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.removePerson(orgId, id),
    onSuccess: (res, { name }) => {
      setNote(
        res?.vacatedEcoRoles?.length
          ? `Removed ${name}. This also vacated: ${res.vacatedEcoRoles.join(', ')}.`
          : `Removed ${name}. They will no longer be alerted.`,
      );
      invalidatePeople();
    },
    onError: (e: Error) => setNote(e.message),
  });
  const addToTeam = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.addTeamMember(orgId, teamId, userId),
    onSuccess: () => {
      invalidatePeople();
      invalidateTeams();
    },
    onError: (e: Error) => setNote(e.message),
  });
  const removeFromTeam = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.removeTeamMember(orgId, teamId, userId),
    onSuccess: () => {
      invalidatePeople();
      invalidateTeams();
    },
    onError: (e: Error) => setNote(e.message),
  });

  // A failed roster load must not read as an empty roster (isError→LoadError,
  // never a false empty).
  if (people.isError && !people.data)
    return (
      <Directory
        title="People"
        typeColumnLabel="People groups"
        types={[{ id: 'all', label: 'All people' }]}
        activeType="all"
        onSelectType={() => undefined}
      >
        <LoadError onRetry={() => void people.refetch()}>
          Couldn’t load your people. This is a load error, not an empty list — retry.
        </LoadError>
      </Directory>
    );
  if (people.isLoading || !people.data) return <Spinner />;

  const members = people.data.members;
  const pendingInvitations = people.data.pendingInvitations;
  const counts = people.data.counts;
  const active = members.filter((m) => m.deprovisioned_at === null);
  const allTeams = teams.data?.teams ?? [];

  // Phase 2 — the cohort column. "Administrators" folds owner/admin/viewer.
  const roleCount = (r: string) => active.filter((m) => m.role === r).length;
  const adminCount = active.filter((m) => ['owner', 'admin', 'viewer'].includes(m.role)).length;
  const types: DirectoryType[] = [
    { id: 'all', label: 'All people', count: active.length },
    { id: 'staff', label: 'Staff', count: roleCount('staff') },
    { id: 'occupants', label: 'Occupants', count: roleCount('occupant') },
    { id: 'admins', label: 'Administrators', count: adminCount },
    ...allTeams.map((t) => ({
      id: `team:${t.id}`,
      label: t.name,
      count: active.filter((m) => m.teams.some((x) => x.id === t.id)).length,
    })),
  ];

  const siteOptions = [
    { value: '', label: 'All sites' },
    ...(sites.data?.sites ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];

  const q = search.trim().toLowerCase();
  const anyFilter = activeType !== 'all' || Boolean(site) || Boolean(q);

  // Which cohort does an intended role belong to (used to place both members and
  // pending invitations in the same cohort column).
  const inCohort = (role: string) => {
    if (activeType === 'all') return true;
    if (activeType === 'staff') return role === 'staff';
    if (activeType === 'occupants') return role === 'occupant';
    if (activeType === 'admins') return ['owner', 'admin', 'viewer'].includes(role);
    return false; // a team cohort — only members carry teams (handled below)
  };

  const filteredMembers = active.filter((m) => {
    if (activeType.startsWith('team:')) {
      if (!m.teams.some((t) => t.id === activeType.slice(5))) return false;
    } else if (!inCohort(m.role)) {
      return false;
    }
    if (site && !m.sites.some((s) => s.id === site)) return false;
    if (q && !m.label.toLowerCase().includes(q)) return false;
    return true;
  });

  // "Awaiting reply" is a status affordance, not a section: pending invitations
  // are rows in the same list, respecting the cohort (by intended role) and the
  // search (by email). A team cohort or a site filter has nothing to match a
  // pending invitation, so pending rows drop out of those views.
  const filteredPending =
    activeType.startsWith('team:') || Boolean(site)
      ? []
      : pendingInvitations.filter(
          (i) => inCohort(i.role) && (!q || i.email.toLowerCase().includes(q)),
        );

  // Selection is over active, non-owner members in view (Owner moves through the
  // transfer flow in Settings and cannot be bulk-managed).
  const selectable = filteredMembers.filter((m) => m.role !== 'owner');
  const selectedInView = selectable.filter((m) => selected.has(m.user_id));
  const allSelected = selectable.length > 0 && selectedInView.length === selectable.length;
  const someSelected = selectedInView.length > 0 && !allSelected;

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) selectable.forEach((m) => next.delete(m.user_id));
      else selectable.forEach((m) => next.add(m.user_id));
      return next;
    });

  // The detail reads the person from the live members, so a change reflects on
  // re-fetch; if they are removed, the overlay closes rather than lingering.
  const detailPerson = detailId
    ? (members.find((m) => m.user_id === detailId && m.deprovisioned_at === null) ?? null)
    : null;

  // Bulk actions reuse the single-person endpoints in sequence — no batch
  // endpoint — with one clear result line; a partial failure is counted.
  async function runBulk(label: string, fn: (userId: string) => Promise<unknown>) {
    const ids = selectedInView.map((m) => m.user_id);
    if (ids.length === 0) return;
    setBulkPending(true);
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await fn(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkPending(false);
    setSelected(new Set());
    invalidatePeople();
    invalidateTeams();
    setNote(
      `${label}: ${ok} ${ok === 1 ? 'person' : 'people'}${failed ? `, ${failed} could not be updated` : ''}.`,
    );
  }

  const selectAll = (
    <input
      type="checkbox"
      aria-label="Select all people"
      checked={allSelected}
      ref={(el) => {
        if (el) el.indeterminate = someSelected;
      }}
      onChange={toggleAll}
      disabled={selectable.length === 0}
      className="h-4 w-4 accent-[var(--accent)]"
    />
  );

  const meta = (
    <>
      {counts.active} active
      {anyFilter ? (
        <span className="text-[var(--ink-3)]">
          {' · '}
          {filteredMembers.length} of {active.length} shown
        </span>
      ) : null}
    </>
  );

  const empty = filteredMembers.length === 0 && filteredPending.length === 0;

  return (
    <Directory
      title="People"
      meta={meta}
      typeColumnLabel="People groups"
      types={types}
      activeType={activeType}
      onSelectType={(id) => {
        setActiveType(id);
        setSelected(new Set());
      }}
      search={{ value: search, onChange: setSearch, label: 'Search people', placeholder: 'Name' }}
      filters={
        <div className="w-40">
          <Select label="Site" value={site} onChange={setSite} options={siteOptions} />
        </div>
      }
      actions={
        <>
          <Button variant="secondary" onClick={() => setCta('upload')}>
            Import people
          </Button>
          {canMakeLink ? (
            <Button variant="secondary" onClick={() => setCta('link')}>
              Share a join link
            </Button>
          ) : null}
          <Button variant="primary" onClick={() => setCta('invite')}>
            Invite
          </Button>
        </>
      }
      toolbar={
        selectedInView.length > 0 ? (
          <BulkBar
            count={selectedInView.length}
            teams={allTeams}
            pending={bulkPending}
            onClear={() => setSelected(new Set())}
            onAddTeam={(teamId) =>
              void runBulk('Added to team', (id) => api.addTeamMember(orgId, teamId, id))
            }
            onRemoveFromTeam={(teamId) =>
              void runBulk('Removed from team', (id) => api.removeTeamMember(orgId, teamId, id))
            }
            onChangeRole={(r) => void runBulk('Role changed', (id) => api.setRole(orgId, id, r))}
            onRemove={() => void runBulk('Removed', (id) => api.removePerson(orgId, id))}
          />
        ) : undefined
      }
    >
      {note ? (
        <div className="mb-4">
          <Finding tone="neutral">{note}</Finding>
        </div>
      ) : null}

      {/* Phase 3 — the list, edge-to-edge (D): no card, no "Everyone" title, no
          bounded scroll box. The reach columns ride on the rows and so respect
          the active cohort + search + site automatically. */}
      <section aria-label="People directory">
        {empty ? (
          <Empty>
            {anyFilter
              ? 'Nobody matches this view. Choose “All people”, or clear the site and search, to see everyone.'
              : 'No one has been added yet. Invite the first person, or import a list, from the top of this page.'}
          </Empty>
        ) : (
          // The list shows only what an admin scans for (owner fix 2026-09-10;
          // column order + widths owner fix 2026-09-21): Name, Email, Team(s),
          // Notifications (only the channels actually on file), Role. The old
          // Coverage wording, the three split channel columns, Site(s), Warden
          // and On-site are gone — On-site is near-always empty (few are signed
          // in), and Coverage read as jargon. The header row is sticky (below
          // the screen header) so the labels stay put while the roster scrolls.
          // Full-bleed wrapper (Trav 2026-09-19 — "the column headers bar should
          // run to the edge"): {@link FullBleedList} pulls the header strip and
          // row separators out to the sheet edges so the list reads as one
          // edge-to-edge surface under the full-width band, while its first/last
          // cell padding puts the CONTENT back in line with the band. Scoped to
          // the table; the note above and legend below stay inset. Shared with
          // Teams and Locations so the four rail lists read the same way.
          <FullBleedList>
            <Table
              head={[selectAll, 'Name', 'Email', 'Team(s)', 'Notifications', 'Role', '']}
              stickyHeader
            >
              {filteredMembers.map((m) => (
                <PersonRow
                  key={m.user_id}
                  person={m}
                  selected={selected.has(m.user_id)}
                  open={detailId === m.user_id}
                  onToggle={() => toggleOne(m.user_id)}
                  onOpen={() => setDetailId(m.user_id)}
                />
              ))}
              {filteredPending.map((i) => (
                <PendingRow
                  key={i.id}
                  invitation={i}
                  orgId={orgId}
                  onNote={setNote}
                  onChanged={invalidatePeople}
                />
              ))}
            </Table>
          </FullBleedList>
        )}
      </section>

      <p className="mt-4 max-w-[640px] text-[13px] text-[var(--ink-2)]">
        Each person is <strong>Active</strong> (accepted, on your list) or <strong>Invited</strong>{' '}
        (asked, not yet accepted — Resend from their row). <strong>Provisioned</strong> marks
        someone added by Entra or a CSV import rather than accepting an invite themselves. The
        notifications column shows the channels held <em>on file</em> — App, SMS or Email; one that
        is not shown has nothing on file for it (no device, number or address). Someone on no
        channel at all is the person who ends up unaccounted for. Holding a channel is not the same
        as a live alert reaching them: until your organisation is confirmed, only people who have
        accepted are included in a live send.
      </p>

      {detailPerson ? (
        <PersonDetail
          orgId={orgId}
          person={detailPerson}
          allTeams={allTeams}
          allMembers={active}
          sites={sites.data?.sites ?? []}
          guardianFollows={guardianFollows.data?.follows ?? []}
          rolePending={changeRole.isPending}
          onClose={() => setDetailId(null)}
          onNote={setNote}
          onChangeRole={(r) => changeRole.mutate({ userId: detailPerson.user_id, role: r })}
          onAddTeam={(teamId) => addToTeam.mutate({ teamId, userId: detailPerson.user_id })}
          onRemoveTeam={(teamId) => removeFromTeam.mutate({ teamId, userId: detailPerson.user_id })}
          onRemove={() =>
            removePerson.mutate({ id: detailPerson.user_id, name: detailPerson.label })
          }
          onGuardianChanged={() =>
            void qc.invalidateQueries({ queryKey: ['guardian-follows', orgId] })
          }
        />
      ) : null}

      {/* The primary CTAs, in the right overlay (F) — never an inline form. */}
      {cta === 'invite' ? (
        <InspectOverlay
          ariaLabel="Invite somebody"
          title="Invite somebody"
          subtitle="Send an invitation by email"
          onClose={() => setCta('none')}
        >
          <Invite
            orgId={orgId}
            bare
            onNote={(text) => setNote(text)}
            onInvited={() => {
              invalidatePeople();
              setCta('none');
            }}
          />
        </InspectOverlay>
      ) : null}

      {cta === 'link' ? (
        <InspectOverlay
          ariaLabel="Share a join link"
          title="Share a join link"
          subtitle="Everyone who joins this way is an occupant"
          onClose={() => setCta('none')}
        >
          <JoinLinkPanel orgId={orgId} />
        </InspectOverlay>
      ) : null}

      {cta === 'upload' ? (
        <InspectOverlay
          ariaLabel="Import people"
          title="Import people"
          subtitle="Add many people at once from a CSV"
          width="wide"
          onClose={() => setCta('none')}
        >
          <LoadARoster orgId={orgId} />
        </InspectOverlay>
      ) : null}
    </Directory>
  );
}

/** The eight AS 3745 ECO roles, in their human wording (migration 0014). */
const WARDEN_LABELS: Record<string, string> = {
  chief_warden: 'Chief Warden',
  deputy_chief_warden: 'Deputy Chief Warden',
  communications_officer: 'Communications Officer',
  area_warden: 'Area Warden',
  floor_warden: 'Floor Warden',
  warden: 'Warden',
  first_aid_officer: 'First Aid Officer',
  emergency_coordinator: 'Emergency Coordinator',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  viewer: 'Viewer',
  staff: 'Staff',
  occupant: 'Occupant',
};

/** The four assignable roles — Owner is absent (it moves through the transfer flow). */
const ASSIGNABLE_ROLES: { value: 'admin' | 'viewer' | 'staff' | 'occupant'; label: string }[] = [
  { value: 'staff', label: 'Staff' },
  { value: 'occupant', label: 'Occupant' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'admin', label: 'Administrator' },
];

type AssignableRole = 'admin' | 'viewer' | 'staff' | 'occupant';

/** The raw-select styling shared by the directory's inline controls. */
const SELECT_CLASS =
  'h-8 rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-2 text-[13px] text-[var(--ink)] outline-none focus:border-[var(--focus)] disabled:opacity-45';

/**
 * The bulk-action bar, shown in the shared sticky header the moment anyone is
 * selected. It reaches the same endpoints the per-person detail does. The
 * destructive action is guarded by an inline confirm — a selection can be large,
 * and a mis-click empties a chunk of the roster silently.
 */
function BulkBar({
  count,
  teams,
  pending,
  onClear,
  onAddTeam,
  onRemoveFromTeam,
  onChangeRole,
  onRemove,
}: {
  count: number;
  teams: api.Team[];
  pending: boolean;
  onClear: () => void;
  onAddTeam: (teamId: string) => void;
  onRemoveFromTeam: (teamId: string) => void;
  onChangeRole: (role: AssignableRole) => void;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper-3)] px-3 py-1.5">
      <span className="mr-1 text-[13px] font-medium">{count} selected</span>

      <select
        className={SELECT_CLASS}
        value=""
        disabled={pending}
        aria-label="Change role for selected people"
        onChange={(e) => {
          if (e.target.value) onChangeRole(e.target.value as AssignableRole);
        }}
      >
        <option value="">Change role…</option>
        {ASSIGNABLE_ROLES.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </select>

      <select
        className={SELECT_CLASS}
        value=""
        disabled={pending || teams.length === 0}
        aria-label="Add selected people to a team"
        onChange={(e) => {
          if (e.target.value) onAddTeam(e.target.value);
        }}
      >
        <option value="">Add to team…</option>
        {teams.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      <select
        className={SELECT_CLASS}
        value=""
        disabled={pending || teams.length === 0}
        aria-label="Remove selected people from a team"
        onChange={(e) => {
          if (e.target.value) onRemoveFromTeam(e.target.value);
        }}
      >
        <option value="">Remove from team…</option>
        {teams.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      {confirming ? (
        <span className="inline-flex items-center gap-2">
          <span className="text-[14px]">Remove {count}?</span>
          <Button
            variant="primary"
            danger
            disabled={pending}
            label={`Confirm removing ${count} people`}
            onClick={() => {
              onRemove();
              setConfirming(false);
            }}
          >
            Confirm remove
          </Button>
          <Button variant="quiet" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </span>
      ) : (
        <Button
          variant="quiet"
          disabled={pending}
          label={`Remove ${count} selected people`}
          onClick={() => setConfirming(true)}
        >
          Remove
        </Button>
      )}

      <Button variant="quiet" disabled={pending} label="Clear the selection" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

/** One person in the directory: a checkbox, the display columns, the reach
 *  columns (App / SMS / Email / Coverage), and the way into the detail. */
function PersonRow({
  person,
  selected,
  open,
  onToggle,
  onOpen,
}: {
  person: api.PersonListEntry;
  selected: boolean;
  /** True while this person's inspect overlay is open — keeps the row highlighted. */
  open: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const m = person;
  const isOwner = m.role === 'owner';
  // The channels that actually reach this person, named for the hover/title and
  // the screen-reader text. Empty = nobody can be notified — the one reach fact
  // that matters at a glance, so it reads as "Off" in alert tone.
  const channels = [
    m.reach.app ? 'App' : null,
    m.reach.sms ? 'SMS' : null,
    m.reach.email ? 'Email' : null,
  ].filter((x): x is string => x !== null);
  const missing = ['App', 'SMS', 'Email'].filter((c) => !channels.includes(c));
  const notified = channels.length > 0;
  // The accessible/hover truth: which channels we hold and, positively, which we
  // do not — so a screen reader or a hover names the gap rather than leaving the
  // struck chip to carry it.
  const reachTitle = notified
    ? `On file: ${channels.join(', ')}${missing.length ? `. No ${missing.join(', ')} on file.` : ''}`
    : 'No channel on file';
  // The name column already shows the label; only show the email column when it
  // adds something (a person with no display name has label === email, and
  // repeating it read as the same address twice — the #132 lesson).
  const emailCol = m.email && m.email !== m.label ? m.email : null;

  return (
    <Row active={open}>
      <Cell>
        <input
          type="checkbox"
          checked={selected}
          disabled={isOwner}
          onChange={onToggle}
          aria-label={
            isOwner ? `${m.label} is the owner and cannot be selected` : `Select ${m.label}`
          }
          className="h-4 w-4 accent-[var(--accent)] disabled:opacity-40"
        />
      </Cell>
      {/* Name leads the row and gets the room (owner ask 2026-09-21 — "the
          name one should be bigger"): a floor width plus a heavier weight, so
          the person reads first and the metadata reads second. */}
      <Cell>
        <span className="flex min-w-[220px] items-center gap-3">
          <Avatar name={m.label} photoUrl={m.photo_url ?? null} />
          <span className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={onOpen}
              aria-label={`View and manage ${m.label}`}
              className="text-left text-[14px] font-medium text-[var(--accent)] underline-offset-2 hover:underline"
            >
              {m.label}
            </button>
            {/* The status word every row now carries (owner ask): a member reads
                "Active" against an invitation's "Invited", so the two are told
                apart at a glance rather than inferred. A person added by Entra
                (SCIM) or a CSV import — rather than accepting an invite
                themselves — carries a quiet "Provisioned" origin tag beside it. */}
            <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
              {/* Derived from the server's status, not a literal — PersonRow
                  only receives active members today, but reading `m.status`
                  means a future "show removed people here" change relabels them
                  rather than silently calling a removed person "Active". */}
              {m.status === 'active' ? 'Active' : 'Removed'}
              {m.provisioned ? (
                <span
                  title="Added by Entra (SCIM) or a CSV import, not a self-accepted invite"
                  className="inline-flex items-center rounded-[var(--radius-1)] bg-[var(--paper-3)] px-1.5 py-px text-[11px] text-[var(--ink-2)]"
                >
                  Provisioned
                </span>
              ) : null}
            </span>
          </span>
        </span>
      </Cell>
      <Cell>
        {emailCol ? (
          // Bounded + truncated so a long government address does not eat the row
          // (Trav 2026-09-19 — "so much room for the email and everything else is
          // cramped"). `title` keeps the full value on hover; it also lives in the
          // person detail pane. The slack now goes to the trailing actions cell,
          // so the columns pack together instead of spreading.
          <span
            className="block max-w-[22rem] truncate text-[14px] text-[var(--ink-2)]"
            title={emailCol}
          >
            {emailCol}
          </span>
        ) : (
          <InspectMuted>—</InspectMuted>
        )}
      </Cell>
      {/* Team(s) directly after Email (owner ask 2026-09-21). */}
      <Cell>
        {m.teams.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {m.teams.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center rounded-[var(--radius-1)] bg-[var(--paper-3)] px-2 py-0.5 text-[13px] text-[var(--ink)]"
              >
                {t.name}
              </span>
            ))}
          </div>
        ) : (
          <InspectMuted>—</InspectMuted>
        )}
      </Cell>
      {/* The notifications column shows ONLY the channels we hold ON FILE for
          this person (owner ask 2026-09-21 — never a struck-out chip for one we
          do not: a crossed-out word is noise on every row that matters and the
          gap it marked is carried by the hover/sr text instead). This is "what
          channel do we hold", not "will a live alert reach them" — for an
          unverified org a member who has not consented is not in a live
          snapshot regardless (verified-org gate); the legend says so. No
          channel at all is the person who ends up unaccounted for, so it reads
          as an alert.

          The visible chips are `aria-hidden`; the sr-only text carries the full
          truth — which channels are on file and, when one is missing, which. */}
      <Cell>
        {notified ? (
          <span className="flex min-w-[150px] flex-wrap items-center gap-1.5" title={reachTitle}>
            <span aria-hidden className="flex flex-wrap items-center gap-1.5">
              {channels.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center rounded-[var(--radius-1)] bg-[var(--paper-3)] px-2 py-0.5 text-[12px] text-[var(--ink)]"
                >
                  {c}
                </span>
              ))}
            </span>
            <span className="sr-only">{reachTitle}</span>
          </span>
        ) : (
          <span title="No notification channel">
            <Status tone="alert">No channel</Status>
            <span className="sr-only"> — no channel; cannot be notified</span>
          </span>
        )}
      </Cell>
      <Cell>{ROLE_LABELS[m.role] ?? m.role}</Cell>
      {/* The trailing cell is the flexible one (`wide`): it absorbs the row's
          slack so the content columns pack together on the left instead of
          spreading. No View button lives here any more — the name opens the
          person's panel, and a second control saying so was noise (owner ask
          2026-09-21). Kept as a column because a pending invitation's
          Resend/Remove still sit here. */}
      <Cell wide>{null}</Cell>
    </Row>
  );
}

/**
 * A pending invitation, folded into the list as a status affordance rather than
 * a stacked "Awaiting reply" section (B). It carries no role/team/on-site to
 * manage — only the intended access and the way to chase or withdraw it.
 */
function PendingRow({
  invitation,
  orgId,
  onNote,
  onChanged,
}: {
  invitation: api.PendingInvitation;
  orgId: string;
  onNote: (text: string) => void;
  onChanged: () => void;
}) {
  const i = invitation;
  const resend = useMutation({
    mutationFn: () => api.resendInvitation(orgId, i.id),
    onSuccess: () => {
      onNote('A fresh invitation is on its way. The earlier link no longer works.');
      onChanged();
    },
    onError: (e: Error) => onNote(e.message),
  });
  const withdraw = useMutation({
    mutationFn: () => api.removePerson(orgId, i.id),
    onSuccess: () => {
      onNote('Invitation withdrawn.');
      onChanged();
    },
    onError: (e: Error) => onNote(e.message),
  });

  return (
    <Row>
      <Cell>
        {/* Not selectable — an invitation is not a roster member. */}
        <span className="sr-only">Not selectable</span>
      </Cell>
      {/* Columns mirror PersonRow: Name, Email, Team(s), Notifications, Role,
          actions. An invitation's identity is its email, shown as the name with
          the pending state beneath it; it has no reach yet, and its team is the
          one the admin named at invite time (joined when they accept). */}
      <Cell>
        <span className="flex min-w-[220px] items-center gap-3">
          <Avatar name={i.email} />
          <span className="flex flex-col gap-0.5">
            <span className="text-[14px] font-medium text-[var(--ink)]">{i.email}</span>
            {/* The status word, matching the members' "Active": every row says
                what it is. "Invited" is the state; expired is a modifier on it,
                and Resend is right there in the actions. */}
            <Status tone="attention">{i.expired ? 'Invited · expired' : 'Invited'}</Status>
          </span>
        </span>
      </Cell>
      <Cell>
        <InspectMuted>—</InspectMuted>
      </Cell>
      <Cell>
        {i.team_name ? (
          <span
            className="inline-flex items-center rounded-[var(--radius-1)] bg-[var(--paper-3)] px-2 py-0.5 text-[13px] text-[var(--ink-2)]"
            title="Joins this team when they accept"
          >
            {i.team_name}
          </span>
        ) : (
          <InspectMuted>—</InspectMuted>
        )}
      </Cell>
      <Cell>
        <InspectMuted>—</InspectMuted>
      </Cell>
      <Cell>{ROLE_LABELS[i.role] ?? i.role}</Cell>
      <Cell wide>
        <span className="inline-flex items-center gap-2">
          <Button
            variant="quiet"
            disabled={resend.isPending}
            label={`Resend the invitation to ${i.email}`}
            onClick={() => resend.mutate()}
          >
            Resend
          </Button>
          <Button
            variant="quiet"
            label={`Withdraw the invitation to ${i.email}`}
            onClick={() => withdraw.mutate()}
          >
            Remove
          </Button>
        </span>
      </Cell>
    </Row>
  );
}

/**
 * The person detail — a slide-in panel over the map, and the full management
 * surface for one person: what is known about them, the controls that change
 * them (role, teams, remove), and the per-person **guardian follows** (moved
 * here from a stacked section, B). A redacted person still reads "Redacted".
 */
function PersonDetail({
  orgId,
  person,
  allTeams,
  allMembers,
  sites,
  guardianFollows,
  rolePending,
  onClose,
  onNote,
  onChangeRole,
  onAddTeam,
  onRemoveTeam,
  onRemove,
  onGuardianChanged,
}: {
  orgId: string;
  person: api.PersonListEntry;
  allTeams: api.Team[];
  allMembers: api.PersonListEntry[];
  sites: api.Site[];
  guardianFollows: api.GuardianFollow[];
  rolePending: boolean;
  onClose: () => void;
  onNote: (text: string) => void;
  onChangeRole: (role: AssignableRole) => void;
  onAddTeam: (teamId: string) => void;
  onRemoveTeam: (teamId: string) => void;
  onRemove: () => void;
  onGuardianChanged: () => void;
}) {
  const m = person;
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Removing or demoting an administrator can leave a two-administrator
  // building (migration 0134) with nobody who could authorise a real alert.
  // Warn in words before it happens — never block offboarding (sanity board
  // BLOCKER 1c).
  const activeAdmins = allMembers.filter(
    (x) => x.deprovisioned_at === null && (x.role === 'owner' || x.role === 'admin'),
  ).length;
  const twoPersonSites = sites.filter((x) => x.activation_rule === 'two_person');
  const leavesTwoPersonUnauthorisable =
    (m.role === 'owner' || m.role === 'admin') && activeAdmins <= 2 && twoPersonSites.length > 0;
  const twoPersonWarning = leavesTwoPersonUnauthorisable
    ? `${twoPersonSites.map((x) => x.name).join(', ')} ${twoPersonSites.length === 1 ? 'needs' : 'need'} two administrators for any real alert. Without ${m.label} as an administrator, no real alert could be sent from ${twoPersonSites.length === 1 ? 'it' : 'them'} until another administrator joins.`
    : null;

  const isOwner = m.role === 'owner';
  const inTeamIds = new Set(m.teams.map((t) => t.id));
  const addableTeams = allTeams.filter((t) => !inTeamIds.has(t.id));
  const channels = [
    m.reach.app ? 'App' : null,
    m.reach.sms ? 'SMS' : null,
    m.reach.email ? 'Email' : null,
  ].filter((c): c is string => c !== null);

  return (
    <InspectOverlay
      ariaLabel={`Manage ${m.label}`}
      title={m.label}
      subtitle={ROLE_LABELS[m.role] ?? m.role}
      onClose={onClose}
    >
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2.5 text-[14px]">
        <InspectField label="Reached by">
          {channels.length > 0 ? channels.join(', ') : <InspectMuted>No channel</InspectMuted>}
        </InspectField>
        <InspectField label="Email">
          {m.email ? m.email : <InspectMuted>—</InspectMuted>}
        </InspectField>
        {/* Present only for owners/admins (`workMobilesVisible`, 0138): the
            number an administrator calls to authorise. Absent for a viewer,
            so this row never claims "no number" to someone not told. */}
        {m.workMobile !== undefined ? (
          <InspectField label="Work mobile">
            {m.workMobile ? (
              <>
                {m.workMobile.display}{' '}
                <span className="text-[var(--ink-3)]">
                  · {m.workMobile.verified ? 'Verified' : 'Not verified'}
                </span>
              </>
            ) : (
              <InspectMuted>
                None — if they raise an alert, whoever authorises it can't call them
              </InspectMuted>
            )}
          </InspectField>
        ) : null}
        <InspectField label="Team(s)">
          {m.teams.length > 0 ? (
            m.teams.map((t) => t.name).join(', ')
          ) : (
            <InspectMuted>—</InspectMuted>
          )}
        </InspectField>
        <InspectField label="Site(s)">
          {m.sites.length > 0 ? (
            m.sites.map((s) => s.name).join(', ')
          ) : (
            <InspectMuted>—</InspectMuted>
          )}
        </InspectField>
        <InspectField label="Warden">
          {m.warden.length > 0 ? (
            m.warden.map((w) => WARDEN_LABELS[w] ?? w).join(', ')
          ) : (
            <InspectMuted>—</InspectMuted>
          )}
        </InspectField>
        <InspectField label="On-site">
          {m.onSite === null ? (
            <InspectMuted>Not known</InspectMuted>
          ) : m.onSite ? (
            'On site'
          ) : (
            <span className="text-[var(--ink-3)]">Not on site</span>
          )}
        </InspectField>
      </dl>

      <div className="mt-6 border-t border-[var(--line)] pt-5">
        <h3 className="mb-3 text-[13px] font-[650] tracking-[0.04em] text-[var(--ink-2)] uppercase">
          Manage
        </h3>

        <div className="mb-5">
          {isOwner ? (
            <p className="text-[13px] text-[var(--ink-2)]">
              This person is the Owner. Their role changes through the transfer-ownership flow in
              Settings, not here.
            </p>
          ) : (
            <label className="block max-w-[240px]">
              <span className="mb-1.5 block">
                <span className="text-[11px] leading-[14px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
                  Role
                </span>
              </span>
              <select
                className="h-9 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)] disabled:opacity-45"
                value={m.role}
                disabled={rolePending}
                aria-label={`Role for ${m.label}`}
                onChange={(e) => onChangeRole(e.target.value as AssignableRole)}
              >
                {ASSIGNABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {twoPersonWarning && m.role === 'admin' ? (
                <span className="mt-1.5 block text-[13px] leading-[18px] text-[var(--attention)]">
                  Changing this role: {twoPersonWarning}
                </span>
              ) : null}
            </label>
          )}
        </div>

        <div className="mb-5">
          <span className="mb-1.5 block text-[11px] leading-[14px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
            Teams
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {m.teams.length > 0 ? (
              m.teams.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-[var(--radius-1)] bg-[var(--paper-3)] py-0.5 pr-1 pl-2 text-[13px] text-[var(--ink)]"
                >
                  {t.name}
                  <button
                    type="button"
                    aria-label={`Remove ${m.label} from ${t.name}`}
                    onClick={() => onRemoveTeam(t.id)}
                    className="inline-flex h-5 min-h-[20px] w-5 items-center justify-center rounded-[var(--radius-1)] text-[var(--ink-2)] hover:bg-[var(--line)] hover:text-[var(--ink)]"
                  >
                    <span aria-hidden>&times;</span>
                  </button>
                </span>
              ))
            ) : (
              <InspectMuted>Not in any team.</InspectMuted>
            )}
          </div>
          {addableTeams.length > 0 ? (
            <select
              className={`${SELECT_CLASS} mt-2`}
              value=""
              aria-label={`Add ${m.label} to a team`}
              onChange={(e) => {
                if (e.target.value) onAddTeam(e.target.value);
              }}
            >
              <option value="">Add to team…</option>
              {addableTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {m.workMobile !== undefined && m.deprovisioned_at === null ? (
          <EditWorkMobile orgId={orgId} person={m} onNote={onNote} />
        ) : null}

        <GuardianFollowsForPerson
          orgId={orgId}
          person={m}
          allMembers={allMembers}
          sites={sites}
          follows={guardianFollows}
          onNote={onNote}
          onChanged={onGuardianChanged}
        />

        <div className="border-t border-[var(--line)] pt-4">
          {isOwner ? (
            <p className="text-[13px] text-[var(--ink-2)]">
              The Owner cannot be removed. Transfer ownership first (in Settings).
            </p>
          ) : confirmRemove ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[14px]">
                Remove <strong className="font-medium">{m.label}</strong>? They will no longer be
                alerted.
                {twoPersonWarning ? (
                  <span className="mt-1 block text-[var(--attention)]">{twoPersonWarning}</span>
                ) : null}
              </span>
              <Button
                variant="primary"
                danger
                label={`Confirm removing ${m.label}`}
                onClick={() => {
                  onRemove();
                  setConfirmRemove(false);
                }}
              >
                Confirm remove
              </Button>
              <Button variant="quiet" onClick={() => setConfirmRemove(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="quiet"
              label={`Remove ${m.label}`}
              onClick={() => setConfirmRemove(true)}
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    </InspectOverlay>
  );
}

/**
 * An administrator adds or corrects a colleague's work mobile (migration
 * 0138). Stored unverified — an admin cannot attest that somebody else holds a
 * number — and the person is asked to verify it at their next sign-in. The
 * audit records that it changed, never the number.
 */
function EditWorkMobile({
  orgId,
  person,
  onNote,
}: {
  orgId: string;
  person: api.PersonListEntry;
  onNote: (text: string) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  // The org's own country from the cached /auth/me, so a national number is
  // read where the organisation is unless the admin picks another (0139).
  const orgCountry =
    qc.getQueryData<api.Me>(['me'])?.memberships.find((m) => m.org_id === orgId)?.org_country ??
    null;
  const [region, setRegion] = useState(() => knownRegion(orgCountry));
  const save = useMutation({
    mutationFn: () => api.setMemberWorkMobile(orgId, person.user_id, value.trim(), region),
    onSuccess: () => {
      setEditing(false);
      setValue('');
      onNote(
        `Saved ${person.label}'s work mobile. They'll be asked to verify it when they next sign in.`,
      );
      void qc.invalidateQueries({ queryKey: ['people', orgId] });
    },
  });

  if (!editing) {
    return (
      <div className="mb-5">
        <Button
          label={`${person.workMobile ? 'Change' : 'Add'} ${person.label}'s work mobile`}
          onClick={() => {
            setEditing(true);
            save.reset();
          }}
        >
          {person.workMobile ? 'Change work mobile' : 'Add work mobile'}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="mb-5 max-w-[320px] space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim() || save.isPending) return;
        save.mutate();
      }}
    >
      <RegionPicker value={region} onChange={setRegion} />
      <Field
        label={`${person.label}'s work mobile`}
        value={value}
        onChange={setValue}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder={phonePlaceholder(region)}
        autoFocus
        hint="Saved as not verified until they confirm it with a texted code. We only call it — we won't text it unless your organisation signs them up for SMS alerts."
      />
      {save.isError ? <Finding>{api.errorMessage(save.error)}</Finding> : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!value.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="quiet" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Guardian follows for one person, inside their overlay — never a stacked
 * section, never a cohort (docs/roster-and-presence.md §2). The rule the surface
 * enforces: there is **no self-service path** to following a person, so the org
 * authorises a guardian→person link from the roster it already holds, and
 * revoking is immediate and **silent**. The guardian sees status and the
 * reunification instruction only, never location.
 */
function GuardianFollowsForPerson({
  orgId,
  person,
  allMembers,
  sites,
  follows,
  onNote,
  onChanged,
}: {
  orgId: string;
  person: api.PersonListEntry;
  allMembers: api.PersonListEntry[];
  sites: api.Site[];
  follows: api.GuardianFollow[];
  onNote: (text: string) => void;
  onChanged: () => void;
}) {
  const [guardianId, setGuardianId] = useState('');
  const [siteId, setSiteId] = useState('');

  const mine = follows.filter((f) => f.person_user_id === person.user_id);
  const guardianOptions = allMembers.filter((m) => m.user_id !== person.user_id);

  const authorise = useMutation({
    mutationFn: () =>
      api.authoriseGuardianFollow(orgId, {
        guardianUserId: guardianId,
        personUserId: person.user_id,
        siteId,
      }),
    onSuccess: () => {
      onNote('Authorised. The guardian now sees status and the reunification instruction only.');
      setGuardianId('');
      setSiteId('');
      onChanged();
    },
    onError: (e: Error) => onNote(e.message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeGuardianFollow(orgId, id),
    onSuccess: () => {
      onNote('Revoked. The guardian was not notified.');
      onChanged();
    },
    onError: (e: Error) => onNote(e.message),
  });

  return (
    <div className="mb-5">
      <span className="mb-1.5 block text-[11px] leading-[14px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
        Guardian follows
      </span>
      <p className="mb-2 text-[13px] text-[var(--ink-2)]">
        A guardian you authorise sees whether {person.label} is safe and where to collect them, and{' '}
        <strong>never their location</strong>. Removing one takes effect immediately, and they are
        not told.
      </p>

      {mine.length > 0 ? (
        <ul className="m-0 mb-2 flex list-none flex-col gap-1.5 p-0">
          {mine.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 text-[14px]">
              <span>
                {f.guardian_name ?? f.guardian_email ?? <InspectMuted>unknown</InspectMuted>}
                <span className="text-[var(--ink-3)]"> · {f.site_name}</span>
              </span>
              <Button
                variant="quiet"
                label={`Revoke ${f.guardian_name ?? f.guardian_email ?? 'this guardian'}'s follow — immediate and silent`}
                onClick={() => revoke.mutate(f.id)}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-[13px] text-[var(--ink-3)]">No guardian is authorised yet.</p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-[180px]">
          <Select
            label="Guardian"
            value={guardianId}
            onChange={setGuardianId}
            options={[
              { value: '', label: 'Choose a guardian…' },
              ...guardianOptions.map((g) => ({ value: g.user_id, label: g.label })),
            ]}
          />
        </div>
        <div className="w-[160px]">
          <Select
            label="Site"
            value={siteId}
            onChange={setSiteId}
            options={[
              { value: '', label: 'Choose a site…' },
              ...sites.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </div>
        <Button
          variant="secondary"
          disabled={!guardianId || !siteId || authorise.isPending}
          onClick={() => authorise.mutate()}
        >
          Authorise
        </Button>
      </div>
    </div>
  );
}

/**
 * A small round initials chip for a person row. Up to two initials from the
 * name; where there is none (a redacted person, an email-only row) it falls back
 * to the person glyph. Token-based, decorative (aria-hidden).
 */
/**
 * The person's WORK photo for this org (0166), else their initials. Never the
 * personal photo: with no work photo the organisation sees none.
 */
function Avatar({ name, photoUrl = null }: { name: string; photoUrl?: string | null }) {
  const initials = (name.match(/\b[a-z]/gi) ?? []).slice(0, 2).join('').toUpperCase();
  if (photoUrl) {
    return (
      <img
        aria-hidden
        alt=""
        src={photoUrl}
        className="h-6 w-6 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--paper-3)] text-[10px] font-medium text-[var(--ink-2)]"
    >
      {initials || <PersonIcon size={13} />}
    </span>
  );
}
